import { CloudOff, RefreshCw, AlertTriangle } from 'lucide-react'

import { useSyncStatus } from '@/hooks/use-sync-status'

/**
 * Status banner pinned above the app header.
 *
 * Three modes, prioritised:
 *   1. **Errors** (red) — at least one queued mission has
 *      exhausted automatic retries. Highest priority because the
 *      driver needs to act on it.
 *   2. **Syncing** (blue) — a sync pass is in flight. Shown
 *      whether we got there from "online", "just reconnected", or
 *      "manual retry". Carries a spinning icon for clarity.
 *   3. **Offline** (yellow) — `navigator.onLine === false`.
 *      Tells the driver any new submission will be queued
 *      locally.
 *
 * If none of those apply, the banner renders nothing — we don't
 * want to consume vertical real-estate when everything is
 * boringly fine.
 *
 * Sticky position keeps the banner visible during page scroll;
 * `top-[env(safe-area-inset-top)]` lifts it under the iOS notch.
 */
export function SyncBanner() {
  const { isOnline, syncing, errorCount, pendingCount } = useSyncStatus()

  if (errorCount > 0) {
    return (
      <Banner tone="danger" icon={<AlertTriangle size={16} />}>
        {errorCount} mission{errorCount > 1 ? 's' : ''} en erreur — réessayez
        depuis le tableau de bord.
      </Banner>
    )
  }
  if (syncing) {
    return (
      <Banner tone="info" icon={<RefreshCw size={16} className="animate-spin" />}>
        Synchronisation en cours
        {pendingCount > 0 ? ` (${pendingCount} en attente)` : ''}…
      </Banner>
    )
  }
  if (!isOnline) {
    return (
      <Banner tone="warning" icon={<CloudOff size={16} />}>
        Hors connexion — les missions seront envoyées dès le retour du réseau.
      </Banner>
    )
  }
  return null
}

interface BannerProps {
  tone: 'warning' | 'info' | 'danger'
  icon: React.ReactNode
  children: React.ReactNode
}

const TONE_CLASSES: Record<BannerProps['tone'], string> = {
  warning: 'bg-warning-soft text-warning border-warning',
  info: 'bg-info-soft text-info border-info',
  danger: 'bg-danger-soft text-danger border-danger',
}

function Banner({ tone, icon, children }: BannerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        'sticky z-20 top-[env(safe-area-inset-top)]',
        'border-b-2 px-4 py-2',
        'flex items-center gap-2 text-sm font-medium',
        TONE_CLASSES[tone],
      ].join(' ')}
    >
      <span className="shrink-0" aria-hidden="true">
        {icon}
      </span>
      <span className="flex-1">{children}</span>
    </div>
  )
}
